import { Api, ApiRequest, ResType } from '@api'
import { Logger } from '@utils'
import {
	GetOrderCustomerReqType,
	GetOrderDetailReqType,
	OrderCreationType,
	OrderDeletionType,
	OrderUpdatePathType,
	OrderUpdateType,
} from '@schemas'

export const getOrder = async ({
	params = {},
	body = {},
	query = {},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/order'
		const req = new ApiRequest({
			method: 'get',
			uri: uri,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const addOrder = async ({
	body = {
		seasonId: '',
		customerId: '',
		couponId: '',
		receiverName: '',
		phoneCountryNumber: '',
		phoneNumber: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: '',
		country: '',
	} as OrderCreationType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/order'
		const req = new ApiRequest({
			method: 'post',
			uri: uri,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const deleteOrder = async ({
	params = { orderId: '' } as OrderDeletionType,
	body = {},
	query = {},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/order/${params.orderId}`
		const req = new ApiRequest({
			method: 'delete',
			uri: uri,
			body: body,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const updateOrder = async ({
	params = { orderId: '' } as OrderUpdatePathType,
	body = {
		deliveryCompany: '',
		trackingNumber: '',
		deliveryCompletedAt: '',
	} as OrderUpdateType,
	query = {},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/order/${params.orderId}`
		const req = new ApiRequest({
			method: 'put',
			uri: uri,
			body: body,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)

		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const getOrderCustomer = async ({
	params = {},
	body = {},
	query = {},
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/order/customer'
		const req = new ApiRequest({
			method: 'get',
			uri: uri,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

export const getOrderDetail = async ({
	query = {
		orderId: '',
	} as GetOrderDetailReqType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = '/order/detail'
		const req = new ApiRequest({
			method: 'get',
			uri: uri,
			query: query,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 주문확인 — PREPARED_AT (SHIPPING_PLAN.md §2.3) */
export const prepareOrder = async ({
	params = { orderId: '' } as OrderUpdatePathType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/order/${params.orderId}/prepare`
		const req = new ApiRequest({
			method: 'put',
			uri: uri,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 입금 확인(도매) — PUT /order/:id/paid. 금액을 비우면 주문 합계 그대로 (DISPATCH_PLAN §5) */
export const markOrderPaid = async ({
	params = { orderId: '' } as OrderUpdatePathType,
	body = { amount: null, ref: '' } as { amount: number | null; ref: string },
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const req = new ApiRequest({
			method: 'put',
			uri: `/order/${params.orderId}/paid`,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

/** 환불 본문 — 사유는 필수, `overrideLock` 은 환불 잠금(생산 시작)을 넘길 때만 true */
export interface OrderRefundReqType {
	reason: string
	overrideLock?: boolean
}

/**
 * 환불 — 주문 하나. 결제수단(PayPal · PayVerse) 분기와 게이트웨이 조회는 **서버**가 한다.
 * 응답 `[{ orderId, outcome, gateway, ref, amount, currency, message }]` (ORDER_PLAN §2.2).
 */
export const refundOrder = async ({
	params = { orderId: '' } as OrderUpdatePathType,
	body = { reason: '', overrideLock: false } as OrderRefundReqType,
	customHeaders = {},
} = {}): Promise<ResType | undefined> => {
	try {
		const uri = `/order/${params.orderId}/refund`
		const req = new ApiRequest({
			method: 'post',
			uri: uri,
			body: body,
			customHeaders: customHeaders,
		})

		const result: ResType = await Api.connection(req)
		return result
	} catch (error) {
		Logger.warn(error)
		return undefined
	}
}

const orderApi = {
	prepareOrder,
	markOrderPaid,
	refundOrder,
	getOrder: getOrder,
	addOrder: addOrder,
	deleteOrder: deleteOrder,
	updateOrder: updateOrder,
	getOrderCustomer: getOrderCustomer,
	getOrderDetail: getOrderDetail,
}

export default orderApi
